import { useEffect, useState } from "react";
import api from "@/lib/api";
import { CheckCircle2, RotateCw, Eye } from "lucide-react";
import AdminRecordDrawer from "@/pages/admin/AdminRecordDrawer";

const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");

const PILL = {
  pending: "bg-amber-50 text-amber-800 border-amber-200",
  paid: "bg-emerald-50 text-emerald-800 border-emerald-200",
  reversed: "bg-rose-50 text-rose-800 border-rose-200",
};

const FILTERS = [
  { id: "", label: "All" },
  { id: "pending", label: "Pending" },
  { id: "paid", label: "Paid" },
  { id: "reversed", label: "Reversed" },
];

export default function AdminAmbassadorPayoutsTab() {
  const [data, setData] = useState({ totals: { pending: 0, paid: 0, reversed: 0 }, commissions: [] });
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState("");
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/commissions", { params: filter ? { status: filter } : {} });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load commissions.");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const markPaid = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/commissions/${id}/mark-paid`, { note: null });
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Mark-paid failed.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-payouts-tab">
      <div className="grid sm:grid-cols-3 gap-4">
        <div className="card-flat p-5" data-testid="payouts-total-pending">
          <p className="overline text-amber-700">Pending</p>
          <p className="mt-2 font-display text-3xl text-[var(--ff-text)]">{fmtUsd(data.totals.pending)}</p>
        </div>
        <div className="card-flat p-5" data-testid="payouts-total-paid">
          <p className="overline text-emerald-700">Paid</p>
          <p className="mt-2 font-display text-3xl text-[var(--ff-text)]">{fmtUsd(data.totals.paid)}</p>
        </div>
        <div className="card-flat p-5" data-testid="payouts-total-reversed">
          <p className="overline text-rose-700">Reversed</p>
          <p className="mt-2 font-display text-3xl text-[var(--ff-text)]">{fmtUsd(data.totals.reversed)}</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="payouts-filter">
        {FILTERS.map((f) => (
          <button
            key={f.id || "all"}
            onClick={() => setFilter(f.id)}
            className={`text-sm rounded-full px-4 py-1.5 border ${filter === f.id ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "bg-white text-[var(--ff-text-secondary)] border-[var(--ff-border)] hover:border-[var(--ff-primary)]"}`}
            data-testid={`payouts-filter-${f.id || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <button onClick={load} className="ml-auto inline-flex items-center gap-1.5 text-sm text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid="payouts-refresh">
          <RotateCw size={14} /> Refresh
        </button>
      </div>

      {err && <p className="mt-4 text-sm text-rose-700" data-testid="payouts-err">{err}</p>}

      <div className="mt-6 card-flat overflow-x-auto">
        <table className="min-w-full text-sm" data-testid="payouts-table">
          <thead className="bg-[var(--ff-muted)] text-left">
            <tr>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Date</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Ambassador</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Code</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Gross</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Net</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)] text-right">Commission</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Payout</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Status</th>
              <th className="px-4 py-3 font-medium text-[var(--ff-text-secondary)]">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--ff-border)]">
            {data.commissions.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-[var(--ff-text-muted)]">No commissions yet.</td></tr>
            )}
            {data.commissions.map((c) => (
              <tr key={c.id} data-testid={`payout-row-${c.id}`}>
                <td className="px-4 py-3 text-[var(--ff-text)] whitespace-nowrap">{fmtDate(c.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="text-[var(--ff-text)]">{c.ambassador_name || "—"}</div>
                  <div className="text-xs text-[var(--ff-text-muted)]">{c.ambassador_email}</div>
                </td>
                <td className="px-4 py-3 font-mono text-xs text-[var(--ff-text-secondary)]">{c.ref_code}</td>
                <td className="px-4 py-3 text-right text-[var(--ff-text-secondary)]">{fmtUsd(c.gross_revenue)}</td>
                <td className="px-4 py-3 text-right text-[var(--ff-text-secondary)]">{fmtUsd(c.net_profit)}</td>
                <td className="px-4 py-3 text-right font-medium text-[var(--ff-text)]">{fmtUsd(c.commission_amount)}</td>
                <td className="px-4 py-3 text-xs text-[var(--ff-text-secondary)]">{c.payout_method || "—"}</td>
                <td className="px-4 py-3">
                  <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs ${PILL[c.status] || ""}`}>{c.status}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    {c.status === "pending" ? (
                      <button
                        onClick={() => markPaid(c.id)}
                        disabled={busyId === c.id}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--ff-primary)] hover:underline disabled:opacity-50"
                        data-testid={`mark-paid-${c.id}`}
                      >
                        <CheckCircle2 size={13} /> Mark paid
                      </button>
                    ) : (
                      <span className="text-xs text-[var(--ff-text-muted)]">—</span>
                    )}
                    <button onClick={() => setDetail(c)} className="inline-flex items-center gap-1 text-xs text-[var(--ff-text-secondary)] hover:text-[var(--ff-primary)]" data-testid={`payout-details-${c.id}`}>
                      <Eye size={13} /> Details
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AdminRecordDrawer record={detail} title="Commission detail" onClose={() => setDetail(null)} />
    </div>
  );
}
